import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CellRenderer, CellRendererParams } from '../../core/interfaces/cell-renderer.interface';

/**
 * Avatar Cell Renderer
 *
 * Renders user avatars with initials or images.
 * Supports displaying name next to avatar.
 *
 * @example
 * ```typescript
 * columnDefs: ColDef<User>[] = [
 *   {
 *     field: 'name',
 *     headerName: 'Usuario',
 *     cellRenderer: AvatarRendererComponent,
 *     cellRendererParams: {
 *       imageField: 'avatar',     // Field containing image URL
 *       showName: true,           // Show name next to avatar
 *       size: 'md',               // 'sm' | 'md' | 'lg'
 *       colorSeed: true,          // Generate color based on name
 *     }
 *   }
 * ];
 * ```
 */
@Component({
  selector: 'app-avatar-renderer',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="flex items-center gap-2">
      <!-- Avatar Circle -->
      <div
        class="flex items-center justify-center rounded-full font-semibold"
        [ngClass]="getAvatarSizeClasses()"
        [style.background-color]="getBackgroundColor()">

        <!-- Image Avatar -->
        <img
          *ngIf="imageUrl"
          [src]="imageUrl"
          [alt]="displayName"
          class="w-full h-full rounded-full object-cover"
          (error)="onImageError()" />

        <!-- Initials Avatar -->
        <span *ngIf="!imageUrl" class="text-white" [ngClass]="getInitialsTextSize()">
          {{ getInitials() }}
        </span>
      </div>

      <!-- Name (optional) -->
      <span *ngIf="showName" class="text-sm text-gray-800 dark:text-gray-200">
        {{ displayName }}
      </span>
    </div>
  `,
})
export class AvatarRendererComponent implements CellRenderer<any> {
  @Input() params!: CellRendererParams<any>;

  displayName: string = '';
  imageUrl: string | null = null;
  showName: boolean = true;
  size: 'sm' | 'md' | 'lg' = 'md';

  ngOnInit(): void {
    this.updateConfig();
  }

  ngOnChanges(): void {
    this.updateConfig();
  }

  private updateConfig(): void {
    // Get display name from value
    this.displayName = this.params.value || 'N/A';

    // Get image URL from imageField if specified
    const imageField = this.params.colDef?.cellRendererParams?.imageField;
    if (imageField && this.params.data) {
      this.imageUrl = this.getNestedValue(this.params.data, imageField);
    }

    // Get configuration
    this.showName = this.params.colDef?.cellRendererParams?.showName !== false;
    this.size = this.params.colDef?.cellRendererParams?.size || 'md';
  }

  getInitials(): string {
    if (!this.displayName) return '?';

    const parts = this.displayName.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return this.displayName.substring(0, 2).toUpperCase();
  }

  getBackgroundColor(): string {
    const colorSeed = this.params.colDef?.cellRendererParams?.colorSeed !== false;
    if (!colorSeed) {
      return '#6b7280'; // Default gray
    }

    // Generate color based on name hash
    const colors = [
      '#ef4444', // red
      '#f97316', // orange
      '#f59e0b', // amber
      '#10b981', // green
      '#06b6d4', // cyan
      '#3b82f6', // blue
      '#6366f1', // indigo
      '#8b5cf6', // purple
      '#ec4899', // pink
    ];

    let hash = 0;
    for (let i = 0; i < this.displayName.length; i++) {
      hash = this.displayName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  }

  getAvatarSizeClasses(): string {
    const sizeClasses = {
      sm: 'w-8 h-8',
      md: 'w-10 h-10',
      lg: 'w-12 h-12',
    };
    return sizeClasses[this.size];
  }

  getInitialsTextSize(): string {
    const textSizes = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };
    return textSizes[this.size];
  }

  onImageError(): void {
    // If image fails to load, fall back to initials
    this.imageUrl = null;
  }

  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }
}
